import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  COLUMN_TYPE_LABELS,
  COLUMN_TYPES,
  type ColumnType,
  createTableSchema,
  type TableResponse,
} from '@k-management/shared';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';

interface ColumnDraft {
  label: string;
  type: ColumnType;
  options: string;
}

/** A free table: name, columns and whether it classifies the registry subjects. */
export function NewTableDialog({
  onCreated,
  onClose,
}: {
  onCreated: (table: TableResponse) => void;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<'direct' | 'versioned'>('direct');
  const [isClassification, setIsClassification] = useState(true);
  const [columns, setColumns] = useState<ColumnDraft[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const create = useMutation({
    mutationFn: (body: unknown) => apiRequest<TableResponse>('/tables', { method: 'POST', body }),
    onSuccess: async (table) => {
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
      onCreated(table);
    },
    onError: (error) => showServerError(error, setErrors, setFormError),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const body = {
      name,
      description,
      mode,
      isClassification,
      columns: columns.map((column) => ({
        label: column.label,
        type: column.type,
        ...((column.type === 'select' || column.type === 'tags') && {
          options: column.options
            .split(',')
            .map((option) => option.trim())
            .filter(Boolean),
        }),
      })),
    };
    const result = validate(createTableSchema, body);
    setErrors(result.errors);
    if (Object.keys(result.errors).length === 0) create.mutate(body);
  };

  const setColumn = (index: number, patch: Partial<ColumnDraft>) =>
    setColumns(columns.map((column, i) => (i === index ? { ...column, ...patch } : column)));

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <h2>Nuova tabella libera</h2>
        <div className="form-grid">
          <Field label="Nome" error={errors.name} hint="Es. Settore, Zona, Fonte del contatto">
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Descrizione" error={errors.description}>
            <input value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field label="Modifiche">
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as 'direct' | 'versioned')}
            >
              <option value="direct">Dirette, con storico</option>
              <option value="versioned">Per versioni con data di validità</option>
            </select>
          </Field>
        </div>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={isClassification}
            onChange={(e) => setIsClassification(e.target.checked)}
          />
          Classificazione dei soggetti: diventa colonna, filtro e scelta nella scheda in Anagrafica
        </label>

        <fieldset className="stack">
          <legend>Colonne oltre a codice e descrizione</legend>
          {columns.map((column, index) => (
            <div key={index} className="column-row">
              <input
                aria-label="Nome della colonna"
                placeholder="Nome della colonna"
                value={column.label}
                onChange={(e) => setColumn(index, { label: e.target.value })}
              />
              <select
                aria-label="Tipo"
                value={column.type}
                onChange={(e) => setColumn(index, { type: e.target.value as ColumnType })}
              >
                {COLUMN_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {COLUMN_TYPE_LABELS[type]}
                  </option>
                ))}
              </select>
              {(column.type === 'select' || column.type === 'tags') && (
                <input
                  aria-label="Scelte, separate da virgola"
                  placeholder="Scelte, separate da virgola"
                  value={column.options}
                  onChange={(e) => setColumn(index, { options: e.target.value })}
                />
              )}
              <button
                type="button"
                className="link"
                onClick={() => setColumns(columns.filter((_, i) => i !== index))}
              >
                Rimuovi
              </button>
            </div>
          ))}
          {errors.columns && <span className="field-error">{errors.columns}</span>}
          <button
            type="button"
            className="small"
            onClick={() => setColumns([...columns, { label: '', type: 'text', options: '' }])}
          >
            Aggiungi una colonna
          </button>
        </fieldset>

        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={create.isPending}>
            {create.isPending ? 'Creazione…' : 'Crea tabella'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
